import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Building2, MapPin, ScrollText, Zap } from 'lucide-react';

const LAST_UPDATE = 'Febrero 2026';

export function TermsPage() {
  return (
    <LegalShell title="TÉRMINOS DE SERVICIO" testId="terms-page">
      <Section title="1. Objeto del Contrato">
        <p>Los presentes Términos de Servicio regulan el uso de la plataforma <b>AURA</b>, operada por
        <b> Marketing de Inteligencia Artificial S. de R.L. de C.V.</b> (en adelante, «AURA» o «la Sociedad»),
        con domicilio fiscal en Av. Américas 1545, pisos 21, 22 y 23, Colonia Providencia, Guadalajara, Jalisco, México,
        representada legalmente por su <b>Apoderado Legal, el C. Ricardo Zermeño Mejía</b>.</p>
      </Section>

      <Section title="2. Aceptación">
        <p>Al registrarte, acceder o utilizar cualquier funcionalidad de AURA (web o aplicaciones móviles), aceptas de forma
        expresa e incondicional estos Términos, el Aviso de Privacidad y todas las políticas complementarias publicadas
        en la plataforma. Si no estás de acuerdo, no debes usar el servicio.</p>
      </Section>

      <Section title="3. Naturaleza del Servicio">
        <p>AURA es una plataforma social gamificada donde los usuarios crean un perfil, publican contenido, participan en
        batallas de opinión («Aura Battles»), otorgan reconocimientos («Puntos Sociales»), asisten a eventos y compiten por
        premios mensuales (paquete ELITE).</p>
        <p><b>Los «Puntos AURA» son puntos virtuales de entretenimiento</b>. No tienen valor monetario, no son transferibles
        fuera de la plataforma, no son canjeables por efectivo y no constituyen valor mobiliario, criptoactivo, ni instrumento
        financiero de ningún tipo.</p>
      </Section>

      <Section title="4. Registro y Cuenta">
        <p>Debes ser mayor de <b>13 años</b> para registrarte. Los usuarios entre 13 y 17 años requieren consentimiento verificable de padre, madre o tutor legal.
        Al registrarte declaras que la información proporcionada es veraz, completa y actualizada, y que asumes total responsabilidad por
        toda actividad realizada bajo tu cuenta.</p>
      </Section>

      <Section title="5. Paquetes de Suscripción">
        <p><b>FREEMIUM:</b> acceso gratuito y perpetuo a las funcionalidades base.</p>
        <p><b>ELITE:</b> pago único de USD $9.00 (o su equivalente en MXN) que otorga acceso de por vida a los beneficios ELITE,
        incluyendo participación en las competencias por puntos mensuales por premios físicos (gadgets, smartphones). El pago
        se procesa a través de Stripe. No hay renovaciones automáticas.</p>
      </Section>

      <Section title="6. Competencias por Puntos y Premios">
        <p>El programa de premios ELITE inicia oficialmente en <b>Octubre 2026</b>. Cada mes, los 5 usuarios ELITE con mayor
        puntaje acumulado (exclusivamente de batallas) recibirán los premios publicados por AURA para ese período. Los premios
        son entregados directamente al ganador; los impuestos aplicables corren por cuenta del ganador conforme a la legislación
        mexicana. AURA se reserva el derecho de sustituir premios por otros de valor equivalente cuando exista causa justificada.</p>
      </Section>

      <Section title="7. Conducta del Usuario">
        <p>Está prohibido publicar contenido ilegal, violento, sexual explícito, discriminatorio, incitador al odio, engañoso,
        que infrinja derechos de terceros o que ataque a menores. AURA puede moderar, suspender o eliminar contenido o cuentas
        que violen estas reglas, sin previo aviso.</p>
      </Section>

      <Section title="8. Propiedad Intelectual">
        <p>La marca AURA, el logotipo, los diseños, el código fuente y las funcionalidades son propiedad exclusiva de la Sociedad.
        El usuario conserva la propiedad del contenido que publica, pero otorga a AURA una <b>licencia mundial, no exclusiva,
        libre de regalías y sublicenciable</b> para alojar, distribuir, mostrar y promocionar dicho contenido dentro de la plataforma.</p>
      </Section>

      <Section title="9. Limitación de Responsabilidad">
        <p>AURA se ofrece «tal cual». En la máxima medida permitida por la ley aplicable, la Sociedad no será responsable
        por daños indirectos, incidentales, consecuenciales, lucro cesante, pérdida de datos, ni por interrupciones del servicio
        derivadas de fuerza mayor o mantenimiento programado.</p>
      </Section>

      <Section title="10. Modificaciones">
        <p>AURA puede modificar estos Términos en cualquier momento. Los cambios se publicarán en la plataforma y entrarán en vigor
        al momento de su publicación. El uso continuado del servicio implica aceptación de los cambios.</p>
      </Section>

      <Section title="11. Terminación">
        <p>Puedes cancelar tu cuenta en cualquier momento desde tu perfil. AURA puede suspender o terminar tu cuenta por incumplimiento
        de estos Términos, fraude, uso indebido o solicitud de autoridad competente.</p>
      </Section>

      <Section title="12. Legislación Aplicable y Jurisdicción">
        <p>Estos Términos se rigen por las leyes federales de los <b>Estados Unidos Mexicanos</b>. Cualquier controversia se someterá
        a los tribunales competentes de la Ciudad de Guadalajara, Jalisco, con renuncia expresa a cualquier otro fuero.</p>
      </Section>

      <Section title="13. Contacto Legal">
        <p><b>Razón Social:</b> Marketing de Inteligencia Artificial S. de R.L. de C.V.<br />
        <b>Apoderado Legal:</b> C. Ricardo Zermeño Mejía<br />
        <b>Domicilio:</b> Av. Américas 1545, pisos 21, 22 y 23, Colonia Providencia, Guadalajara, Jalisco, México<br />
        <b>Correo legal:</b> legal@comunidadaura.com</p>
      </Section>

      <div className="text-[11px] text-aura-textDim italic border-t border-white/5 pt-4">
        Última actualización: {LAST_UPDATE}
      </div>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="AVISO DE PRIVACIDAD" testId="privacy-page">
      <p className="text-sm text-aura-textDim mb-4">
        Aviso de Privacidad Integral en cumplimiento con la Ley Federal de Protección de Datos Personales
        en Posesión de los Particulares (LFPDPPP) y su Reglamento.
      </p>

      <Section title="1. Responsable del Tratamiento">
        <p><b>Marketing de Inteligencia Artificial S. de R.L. de C.V.</b> («AURA»), con domicilio en Av. Américas 1545,
        pisos 21, 22 y 23, Colonia Providencia, Guadalajara, Jalisco, México, es la responsable del uso, protección y
        tratamiento de tus datos personales. El <b>Responsable designado</b> conforme a la LFPDPPP es el
        <b> C. Ricardo Zermeño Mejía</b>, en su carácter de Apoderado Legal de la Sociedad.</p>
      </Section>

      <Section title="2. Datos Personales que Recabamos">
        <p>Podemos recabar las siguientes categorías de datos personales:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Identificación:</b> nombre completo, nombre de usuario, fecha de nacimiento, fotografía de perfil.</li>
          <li><b>Contacto:</b> número de celular, correo electrónico.</li>
          <li><b>Ubicación:</b> ciudad, estado y país (sin coordenadas exactas a menos que las autorices).</li>
          <li><b>Cuenta:</b> contraseña cifrada, identificador de Google (si usas login social).</li>
          <li><b>Actividad:</b> publicaciones, comentarios, reacciones, batallas, votos, reconocimientos, historial de puntos.</li>
          <li><b>Facturación (paquete ELITE):</b> AURA no almacena datos de tu tarjeta; se procesan directamente por Stripe, Inc.</li>
          <li><b>Datos técnicos:</b> dirección IP, tipo de dispositivo, navegador, cookies y logs de uso.</li>
        </ul>
        <p><b>No recabamos datos personales sensibles</b> (origen racial, estado de salud, creencias religiosas, opiniones políticas, orientación sexual, etc.).</p>
      </Section>

      <Section title="3. Finalidades del Tratamiento">
        <p><b>Finalidades primarias (necesarias para el servicio):</b></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Creación y administración de tu cuenta y perfil.</li>
          <li>Operación de la red social: feed, batallas, eventos, reconocimientos, rankings.</li>
          <li>Procesamiento del pago único ELITE y activación del beneficio.</li>
          <li>Entrega de premios ELITE a los ganadores mensuales.</li>
          <li>Prevención de fraudes, moderación de contenido y cumplimiento de obligaciones legales.</li>
          <li>Soporte técnico y comunicaciones transaccionales.</li>
        </ul>
        <p><b>Finalidades secundarias (opcionales, puedes negarlas):</b></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Envío de comunicaciones promocionales y novedades.</li>
          <li>Análisis estadísticos agregados para mejorar el servicio.</li>
        </ul>
      </Section>

      <Section title="4. Fundamento Legal">
        <p>Tratamos tus datos con base en tu consentimiento expreso al aceptar este Aviso durante el registro, y en el
        cumplimiento de la relación contractual entre tú y AURA, conforme a los artículos 6, 8, 15, 16, 17 y 36 de la LFPDPPP.</p>
      </Section>

      <Section title="5. Transferencias">
        <p>AURA puede transferir datos personales a los siguientes terceros sin requerir tu consentimiento adicional
        (art. 37 LFPDPPP):</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Stripe, Inc.</b> — procesamiento de pagos del paquete ELITE.</li>
          <li>Proveedores de infraestructura en la nube y almacenamiento (bajo cláusulas de confidencialidad).</li>
          <li>Autoridades competentes cuando exista requerimiento legal.</li>
        </ul>
      </Section>

      <Section title="6. Tus Derechos ARCO">
        <p>Tienes derecho en todo momento a <b>Acceder, Rectificar, Cancelar u Oponerte</b> al tratamiento de tus datos
        personales, así como a limitar su uso o divulgación y a revocar tu consentimiento. Para ejercerlos, envía una
        solicitud al correo <b>privacidad@comunidadaura.com</b> con:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Nombre completo y correo de tu cuenta AURA.</li>
          <li>Identificación oficial vigente (INE, pasaporte).</li>
          <li>Descripción clara del derecho que deseas ejercer.</li>
        </ul>
        <p>Responderemos en un plazo máximo de 20 días hábiles.</p>
      </Section>

      <Section title="7. Cookies y Tecnologías de Rastreo">
        <p>Utilizamos cookies y almacenamiento local (localStorage) para mantener tu sesión iniciada, recordar preferencias
        de idioma y atribuir referidos. Puedes deshabilitarlas desde tu navegador, aunque algunas funciones podrían dejar de operar.</p>
      </Section>

      <Section title="8. Menores de Edad">
        <p>AURA no está dirigida a menores de 13 años. Los usuarios entre 13 y 17 años requieren autorización verificable
        de padre, madre o tutor. Si detectamos una cuenta de un menor sin autorización, la eliminaremos.</p>
      </Section>

      <Section title="9. Seguridad">
        <p>Aplicamos medidas técnicas, administrativas y físicas razonables para proteger tus datos: cifrado de contraseñas
        con bcrypt, tokens JWT firmados, HTTPS en todas las comunicaciones y controles de acceso internos.</p>
      </Section>

      <Section title="10. Cambios al Aviso de Privacidad">
        <p>Cualquier cambio será publicado en la plataforma y notificado por correo electrónico al menos 15 días antes de su
        entrada en vigor. La versión vigente siempre estará disponible en esta URL.</p>
      </Section>

      <Section title="11. Contacto">
        <p><b>Razón Social:</b> Marketing de Inteligencia Artificial S. de R.L. de C.V.<br />
        <b>Responsable / Apoderado Legal:</b> C. Ricardo Zermeño Mejía<br />
        <b>Domicilio:</b> Av. Américas 1545, pisos 21, 22 y 23, Colonia Providencia, Guadalajara, Jalisco, México<br />
        <b>Correo para ejercicio de derechos ARCO:</b> privacidad@comunidadaura.com</p>
        <p>Puedes también acudir al Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos
        Personales (INAI) si consideras que tu derecho de protección de datos personales ha sido lesionado.</p>
      </Section>

      <div className="text-[11px] text-aura-textDim italic border-t border-white/5 pt-4">
        Última actualización: {LAST_UPDATE}
      </div>
    </LegalShell>
  );
}

// ---------- Shared UI ----------

function LegalShell({ title, testId, children }) {
  return (
    <div className="min-h-screen bg-aura-bg relative overflow-x-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-aura-cyan/15 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6 md:px-10 max-w-4xl mx-auto">
        <Link to="/" className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} /> <span className="text-xs font-display font-bold tracking-widest">INICIO</span>
        </Link>
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-aura-cyan" />
          <span className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">AURA</span>
        </div>
      </header>

      <main data-testid={testId} className="relative max-w-3xl mx-auto px-5 md:px-8 pt-8 pb-24 text-white/85 leading-relaxed">
        <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 mb-3">
          <ScrollText size={14} className="text-aura-cyan" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-display font-semibold text-white/70">Documento legal</span>
        </div>
        <h1 className="font-display font-black text-white text-3xl md:text-4xl leading-[0.95] mb-6">{title}</h1>

        <div className="aura-card p-4 mb-6 border-aura-cyan/25">
          <div className="flex items-start gap-3 text-xs">
            <Building2 size={16} className="text-aura-cyan mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-black text-white">Marketing de Inteligencia Artificial S. de R.L. de C.V.</div>
              <div className="text-aura-textDim mt-1 text-[11px]">
                Apoderado Legal: <span className="text-white/80 font-semibold">C. Ricardo Zermeño Mejía</span>
              </div>
              <div className="text-aura-textDim mt-1 flex items-start gap-1">
                <MapPin size={11} className="mt-0.5" /> Av. Américas 1545, pisos 21, 22 y 23, Colonia Providencia, Guadalajara, Jalisco, México
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 text-sm">{children}</div>

        <div className="mt-10 flex items-center justify-between text-xs">
          <Link to="/terminos" className="text-aura-cyan hover:underline">Términos de Servicio</Link>
          <Link to="/privacidad" className="text-aura-cyan hover:underline">Aviso de Privacidad</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-black text-white text-lg mb-2">{title}</h2>
      <div className="space-y-2 text-white/80">{children}</div>
    </section>
  );
}
